/* eslint-disable react/no-unescaped-entities */
/**
 * Firm settings — Access Control
 *
 * Three tabs: Roles matrix · Teams · User overrides · Audit log
 */
import { useEffect, useState } from "react";
import { Shield, Users, UserCog, History, Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const SCOPES = ["own", "team", "firm", "none"];
const SCOPE_LABEL = { own: "Own only", team: "Team", firm: "Firm-wide", none: "No access" };

export default function AccessControl() {
    const [tab, setTab] = useState("roles");
    return (
        <div className="space-y-6" data-testid="access-control-page">
            <div>
                <div className="overline">Firm Settings</div>
                <h1 className="display-display mt-1 inline-flex items-center gap-3">
                    <Shield className="w-7 h-7 text-primary" strokeWidth={1.5}/> Access Control
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Configure what each role / user can see and do. By default everyone in your firm only sees their
                    own entries (except senior advocates + firm owners who see firm-wide).
                </p>
            </div>

            <div className="flex border-b border-border overflow-x-auto scrollbar-hide">
                <TabBtn active={tab === "roles"} onClick={() => setTab("roles")} testid="ac-tab-roles">
                    <Shield className="w-3.5 h-3.5"/> Role matrix
                </TabBtn>
                <TabBtn active={tab === "teams"} onClick={() => setTab("teams")} testid="ac-tab-teams">
                    <Users className="w-3.5 h-3.5"/> Teams
                </TabBtn>
                <TabBtn active={tab === "overrides"} onClick={() => setTab("overrides")} testid="ac-tab-overrides">
                    <UserCog className="w-3.5 h-3.5"/> User overrides
                </TabBtn>
                <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} testid="ac-tab-audit">
                    <History className="w-3.5 h-3.5"/> Audit log
                </TabBtn>
            </div>

            {tab === "roles" && <RolesTab/>}
            {tab === "teams" && <TeamsTab/>}
            {tab === "overrides" && <OverridesTab/>}
            {tab === "audit" && <AuditTab/>}
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 ${active ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}>
            {children}
        </button>
    );
}

// ============================================================================
function RolesTab() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/access/rules").then(r => setData(r.data)).catch(e => toast.error(apiError(e))); }, []);

    const update = async (role, resource, action, scope) => {
        try {
            await api.post("/access/rules", { role, resource, action, scope });
            toast.success(`Updated ${role} → ${resource}.${action} = ${scope}`);
            const fresh = await api.get("/access/rules");
            setData(fresh.data);
        } catch (e) { toast.error(apiError(e)); }
    };

    if (!data) return <div className="text-center text-muted-foreground text-sm py-12"><Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading…</div>;
    const editable = data.roles.filter(r => r !== "firm_owner" && r !== "managing_partner");
    return (
        <div className="bg-card border border-border" data-testid="role-matrix">
            <header className="px-6 py-4 border-b border-border">
                <h2 className="text-sm uppercase tracking-[0.15em] text-foreground">Role permissions</h2>
                <p className="text-[11px] text-muted-foreground mt-1">For each role × resource × action, pick a scope. firm_owner / managing_partner are always firm-wide and cannot be reduced.</p>
            </header>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        <tr>
                            <th className="text-left px-4 py-2">Role</th>
                            <th className="text-left px-4 py-2">Resource</th>
                            {data.actions.map(a => <th key={a} className="text-left px-4 py-2">{a}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {editable.flatMap(role => data.resources.map(resource => (
                            <tr key={`${role}-${resource}`} className="border-t border-border" data-testid={`row-${role}-${resource}`}>
                                <td className="px-4 py-2 text-foreground whitespace-nowrap">{role.replace(/_/g, " ")}</td>
                                <td className="px-4 py-2 text-muted-foreground">{resource}</td>
                                {data.actions.map(a => {
                                    const current = data.matrix?.[role]?.[resource]?.[a] || "own";
                                    return (
                                        <td key={a} className="px-4 py-2">
                                            <select value={current} onChange={e => update(role, resource, a, e.target.value)}
                                                data-testid={`scope-${role}-${resource}-${a}`}
                                                className="bg-background border border-border px-2 py-1 text-xs text-foreground">
                                                {SCOPES.map(s => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
                                            </select>
                                        </td>
                                    );
                                })}
                            </tr>
                        )))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================================
function TeamsTab() {
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", member_user_ids: [] });

    const load = async () => {
        try {
            const [t, u] = await Promise.all([api.get("/access/teams"), api.get("/users")]);
            setTeams(t.data); setUsers(u.data);
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        setCreating(true);
        try {
            await api.post("/access/teams", form);
            setForm({ name: "", member_user_ids: [] });
            await load();
            toast.success("Team created");
        } catch (e) { toast.error(apiError(e)); }
        finally { setCreating(false); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this team?")) return;
        await api.delete(`/access/teams/${id}`);
        load();
        toast.success("Team deleted");
    };

    return (
        <div className="space-y-4" data-testid="teams-tab">
            <div className="bg-card border border-border p-5">
                <div className="text-sm uppercase tracking-[0.15em] text-foreground mb-3">New team</div>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Team name (e.g. Litigation Wing)" data-testid="team-name-input"
                    className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                <div className="overline mt-3">Members</div>
                <div className="mt-2 max-h-48 overflow-y-auto border border-border">
                    {users.filter(u => u.role !== "client_portal").map(u => (
                        <label key={u.id} className="flex items-center gap-2 px-3 py-2 border-b border-border text-xs">
                            <input type="checkbox" checked={form.member_user_ids.includes(u.id)}
                                onChange={e => setForm(p => ({ ...p, member_user_ids: e.target.checked ? [...p.member_user_ids, u.id] : p.member_user_ids.filter(x => x !== u.id) }))}
                                data-testid={`team-member-${u.id}`} className="accent-primary"/>
                            <span className="text-foreground">{u.name || u.email}</span>
                            <span className="text-[10px] text-muted-foreground uppercase tracking-[0.1em]">· {(u.role || "").replace(/_/g, " ")}</span>
                        </label>
                    ))}
                </div>
                <button onClick={create} disabled={creating} data-testid="team-create-btn"
                    className="mt-3 px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 disabled:opacity-40">
                    {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>} Create team
                </button>
            </div>
            <div className="bg-card border border-border">
                <header className="px-6 py-4 border-b border-border text-sm uppercase tracking-[0.15em] text-foreground">Existing teams</header>
                {teams.length === 0 && <div className="text-center text-muted-foreground py-8 text-xs">No teams yet</div>}
                {teams.map(t => (
                    <div key={t.id} className="px-6 py-3 border-t border-border flex items-center justify-between" data-testid={`team-row-${t.id}`}>
                        <div>
                            <div className="text-sm text-foreground">{t.name}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{t.members.map(m => m.name).join(", ") || "no members"}</div>
                        </div>
                        <button onClick={() => remove(t.id)} className="text-rose-400 hover:text-rose-300" data-testid={`team-delete-${t.id}`}>
                            <Trash2 className="w-4 h-4"/>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================================
function OverridesTab() {
    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({ user_id: "", resource: "cases", action: "view", scope: "firm", reason: "" });
    const tableCtrl = useTableControls(rows, { accessors: { user: (r) => r.user_name || r.user_id, scope: (r) => SCOPE_LABEL[r.scope] || r.scope } });

    const load = async () => {
        try {
            const [o, u] = await Promise.all([api.get("/access/overrides"), api.get("/users")]);
            setRows(o.data); setUsers(u.data);
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.user_id) { toast.error("Pick a user"); return; }
        try {
            await api.post("/access/overrides", form);
            setForm({ ...form, reason: "" });
            await load();
            toast.success("Override saved");
        } catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Remove this override?")) return;
        await api.delete(`/access/overrides/${id}`);
        load();
        toast.success("Override removed");
    };

    return (
        <div className="space-y-4" data-testid="overrides-tab">
            <div className="bg-card border border-border p-5">
                <div className="text-sm uppercase tracking-[0.15em] text-foreground mb-3">Grant a user-level override</div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <select value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} data-testid="ov-user"
                        className="bg-background border border-border px-3 py-2 text-xs text-foreground">
                        <option value="">Pick user…</option>
                        {users.filter(u => u.role !== "client_portal" && u.role !== "firm_owner").map(u =>
                            <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
                    </select>
                    <select value={form.resource} onChange={e => setForm({ ...form, resource: e.target.value })} data-testid="ov-resource"
                        className="bg-background border border-border px-3 py-2 text-xs text-foreground">
                        {["cases","clients","hearings","invoices","documents","tasks","court_vc_links","audit","chat"].map(r => <option key={r}>{r}</option>)}
                    </select>
                    <select value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} data-testid="ov-action"
                        className="bg-background border border-border px-3 py-2 text-xs text-foreground">
                        {["view","edit","delete"].map(a => <option key={a}>{a}</option>)}
                    </select>
                    <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} data-testid="ov-scope"
                        className="bg-background border border-border px-3 py-2 text-xs text-foreground">
                        {SCOPES.map(s => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
                    </select>
                    <button onClick={create} data-testid="ov-create-btn"
                        className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.15em] inline-flex items-center justify-center gap-2">
                        <Save className="w-3.5 h-3.5"/> Save
                    </button>
                </div>
                <input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Reason (optional, recorded in audit log)"
                    className="mt-2 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs" data-testid="ov-reason"/>
            </div>
            <div className="bg-card border border-border">
                <header className="px-6 py-4 border-b border-border text-sm uppercase tracking-[0.15em] text-foreground">Active overrides</header>
                <table className="w-full text-xs">
                    <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        <tr>
                            <SortableTh field="user" ctrl={tableCtrl} className="text-left px-4 py-2">User</SortableTh>
                            <SortableTh field="resource" ctrl={tableCtrl} className="text-left px-4 py-2">Resource</SortableTh>
                            <SortableTh field="action" ctrl={tableCtrl} className="text-left px-4 py-2">Action</SortableTh>
                            <SortableTh field="scope" ctrl={tableCtrl} className="text-left px-4 py-2">Scope</SortableTh>
                            <SortableTh field="reason" ctrl={tableCtrl} className="text-left px-4 py-2">Reason</SortableTh>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableCtrl.rows.map(r => (
                            <tr key={r.id} className="border-t border-border" data-testid={`ov-row-${r.id}`}>
                                <td className="px-4 py-2 text-foreground">{r.user_name || r.user_id}</td>
                                <td className="px-4 py-2 text-muted-foreground">{r.resource}</td>
                                <td className="px-4 py-2 text-muted-foreground">{r.action}</td>
                                <td className="px-4 py-2 text-emerald-400">{SCOPE_LABEL[r.scope]}</td>
                                <td className="px-4 py-2 text-muted-foreground">{r.reason || "—"}</td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`ov-delete-${r.id}`}><Trash2 className="w-4 h-4"/></button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No overrides</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================================
function AuditTab() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/access/audit").then(r => setRows(r.data)).catch(e => toast.error(apiError(e))); }, []);
    const tableCtrl = useTableControls(rows, { accessors: { payload: (r) => JSON.stringify(r.payload) } });
    return (
        <div className="bg-card border border-border overflow-x-auto" data-testid="audit-tab">
            <header className="px-6 py-4 border-b border-border text-sm uppercase tracking-[0.15em] text-foreground">Permission changes (latest 200)</header>
            <table className="w-full text-xs">
                <thead className="bg-background text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    <tr>
                        <th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Actor</th>
                        <th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Payload</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.id} className="border-t border-border">
                            <td className="px-4 py-2 text-muted-foreground">{fmtDateTime(r.at)}</td>
                            <td className="px-4 py-2 text-foreground">{r.actor}</td>
                            <td className="px-4 py-2 text-primary">{r.action}</td>
                            <td className="px-4 py-2 text-muted-foreground font-mono text-[10px]">{JSON.stringify(r.payload)}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-8">No changes yet</td></tr>}
                </tbody>
            </table>
        </div>
    );
}
